import React, { useState, useCallback, ReactElement } from 'react';
import { IDropDownItem } from 'Framework';
import * as css from './CVLog.module.scss';
import 'src/css/themes.scss';
import { IconSeeMore, IconSortAscending, IconSortDescending, IconPrint } from 'src/content/icons';
import { ConnectedVehicleRec, LOG } from 'src/models';
import { Dialog, ScrollBar, SelectList } from '@framework-controls/core';
import { strings } from 'src/localization';
import { random } from 'lodash';

export interface IProps {
    connectedVehicle: ConnectedVehicleRec;
    printConnectedVehicle: (filter: string) => void;
}

const CVLog = (props: IProps) => {
    const [logOpen, setLogOpen] = useState(false);
    const [sortReversed, setSortReversed] = useState(false);
    const toggleLog = useCallback(() => {
        setLogOpen(!logOpen);
    }, [logOpen]);
    const reverseSort = useCallback(() => {
        setSortReversed(!sortReversed);
    }, [sortReversed]);
    const [typeFilter, setTypeFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState('all');
    const applyTypeFilter = useCallback((id: string, value: string) => {
        setTypeFilter(value);
    }, []);
    const applyDateFilter = useCallback((id: string, value: string) => {
        setDateFilter(value);
    }, []);
    const printCV = useCallback(() => {
        props.printConnectedVehicle('L');
    }, [props.printConnectedVehicle]);
    const [sortIconColumn, setSortIconColumn] = useState('none');
    const moveFocusToDesc = useCallback(() => {
        if (sortIconColumn !== 'desc') {
            setSortReversed(false);
        }
        setSortIconColumn('desc');
    }, [sortIconColumn]);
    const moveFocusToDate = useCallback(() => {
        if (sortIconColumn !== 'date') {
            setSortReversed(false);
        }
        setSortIconColumn('date');
    }, [sortIconColumn]);
    const moveFocusToTime = useCallback(() => {
        if (sortIconColumn !== 'time') {
            setSortReversed(false);
        }
        setSortIconColumn('time');
    }, [sortIconColumn]);
    const moveFocusToUser = useCallback(() => {
        if (sortIconColumn !== 'user') {
            setSortReversed(false);
        }
        setSortIconColumn('user');
    }, [sortIconColumn]);

    let logs: ReactElement[] | null = null;
    if (props.connectedVehicle.log && props.connectedVehicle.log.length > 0) {
        const indexedLogs: LOG[] = [];
        const vehLogs = [...props.connectedVehicle.log];
        let sortedLogs: LOG[] = [];
        if ((sortIconColumn === 'none') || (sortIconColumn === 'date') || (sortIconColumn === 'time')) {
            sortedLogs = vehLogs.sort((a, b) => (a.tstamp > b.tstamp ? 1 : -1));
            if (sortReversed) {
                sortedLogs = vehLogs.sort((a, b) => (b.tstamp > a.tstamp ? 1 : -1));
            }
        }
        if (sortIconColumn === 'desc') {
            sortedLogs = vehLogs.sort((a, b) => (a.desc > b.desc ? 1 : -1));
            if (sortReversed) {
                sortedLogs = vehLogs.sort((a, b) => (b.desc > a.desc ? 1 : -1));
            }
        }
        if (sortIconColumn === 'user') {
            sortedLogs = vehLogs.sort((a, b) => (a.user > b.user ? 1 : -1));
            if (sortReversed) {
                sortedLogs = vehLogs.sort((a, b) => (b.user > a.user ? 1 : -1));
            }
        }
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;
        let lastMonth = currentDate.getMonth();
        if (lastMonth === 0) {
            lastMonth = 12;
        }
        let lastMonth1 = lastMonth - 1;
        if (lastMonth1 === 0) {
            lastMonth1 = 12;
        }
        let lastMonth2 = lastMonth1 - 1;
        if (lastMonth2 === 0) {
            lastMonth2 = 12;
        }
        let lastMonth3 = lastMonth2 - 1;
        if (lastMonth3 === 0) {
            lastMonth3 = 12;
        }
        let lastMonth4 = lastMonth3 - 1;
        if (lastMonth4 === 0) {
            lastMonth4 = 12;
        }
        let lastMonth5 = lastMonth4 - 1;
        if (lastMonth5 === 0) {
            lastMonth5 = 12;
        }
        let index: number = 0;
        sortedLogs.forEach((log) => {
            let keep: Boolean = true;
            if ((typeFilter === 'remote') && (log.desc !== strings.AutomatedVehicleQueryRemote)) {
                keep = false;
            }
            if ((typeFilter === 'present') && (log.desc !== strings.AutomatedVehicleQueryVehiclePresent)) {
                keep = false;
            }
            if ((typeFilter === 'manual') && (log.desc !== strings.ManualVehicleQuery)) {
                keep = false;
            }
            const logYear: Number = Number(log.date.slice(6, 10));
            const logMonth: Number = Number(log.date.slice(0, 2));
            if ((dateFilter === 'year') && (logYear !== (currentYear - 1))) {
                keep = false;
            }
            if ((dateFilter === 'current') && (logMonth !== currentMonth)) {
                keep = false;
            }
            if ((dateFilter === 'last') && (logMonth !== lastMonth)) {
                keep = false;
            }
            if ((dateFilter === 'last6') && ((logMonth !== lastMonth) && (logMonth !== lastMonth1) &&
                (logMonth !== lastMonth2) && (logMonth !== lastMonth3) && (logMonth !== lastMonth4) &&
                (logMonth !== lastMonth5) && (logMonth !== currentMonth))) {
                keep = false;
            }
            if (keep) {
                index = index + 1;
                indexedLogs.push(
                    {
                        index: index,
                        desc: log.desc,
                        date: log.date,
                        time: log.time,
                        user: log.user,
                        tstamp: log.tstamp,
                    },
                );
            }
        });
        logs = indexedLogs.map(log => <span className={log.index % 2 === 0 ? css.logRow : css.logRowOdd}
            id={log.desc + log.date + log.time + log.user}
            key={log.desc + log.date + log.time + log.user + random(99999)}>
            <div className={css.logDesc}>{log.desc}</div>
            <div className={css.logDate}>{log.date}</div>
            <div className={css.logTime}>{log.time}</div>
            <div className={css.logUser}>{log.user}</div>
        </span>);
    }

    let descSortIcon: ReactElement | null = null;
    if (sortIconColumn === 'desc') {
        descSortIcon = <IconSortAscending id='descSortIcon' className={css.logIcon} onClick={() => reverseSort()}/>;
        if (sortReversed) {
            descSortIcon = <IconSortDescending id='descSortIcon2' className={css.logIcon} onClick={() => reverseSort()}/>;
        }
    }

    let dateSortIcon: ReactElement | null = null;
    if (sortIconColumn === 'date') {
        dateSortIcon = <IconSortAscending id='dateSortIcon' className={css.logIcon} onClick={() => reverseSort()}/>;
        if (sortReversed) {
            dateSortIcon = <IconSortDescending id='dateSortIcon2' className={css.logIcon} onClick={() => reverseSort()}/>;
        }
    }

    let timeSortIcon: ReactElement | null = null;
    if (sortIconColumn === 'time') {
        timeSortIcon = <IconSortAscending id='timeSortIcon' className={css.logIcon} onClick={() => reverseSort()}/>;
        if (sortReversed) {
            timeSortIcon = <IconSortDescending id='timeSortIcon2' className={css.logIcon} onClick={() => reverseSort()}/>;
        }
    }

    let userSortIcon: ReactElement | null = null;
    if (sortIconColumn === 'user') {
        userSortIcon = <IconSortAscending id='userSortIcon' className={css.logIcon} onClick={() => reverseSort()}/>;
        if (sortReversed) {
            userSortIcon = <IconSortDescending id='userSortIcon2' className={css.logIcon} onClick={() => reverseSort()}/>;
        }
    }

    // Build the selectList options for the types switcher.
    const logTypes : IDropDownItem[] = [
        { label: strings.AllTypes, value: 'all' },
        { label: strings.AutomatedVehicleQueryRemote, value: 'remote' },
        { label: strings.AutomatedVehicleQueryVehiclePresent, value: 'present' },
        { label: strings.ManualVehicleQuery, value: 'manual' },
    ];

    // Build the selectList options for the dates switcher.
    const logDates : IDropDownItem[] = [
        { label: strings.CurrentMonth, value: 'current' },
        { label: strings.LastMonth, value: 'last' },
        { label: strings.Last6Months, value: 'last6' },
        { label: strings.LastYear, value: 'year' },
        { label: strings.AllDates, value: 'all' },
    ];

    let logArea: ReactElement | null = null;
    if (props.connectedVehicle.log && props.connectedVehicle.log.length > 0) {
        logArea =
            <div>
                <div className={css.filterRow}>
                    <div className={css.typeFilter}>
                        <SelectList
                            id='typeFilter'
                            value='all'
                            size='rg'
                            onChange={applyTypeFilter}
                            dropDownList={logTypes}
                            variant='roundedBorder'
                            disableUnderline={true}
                        />
                    </div>
                    <div className={css.dateFilter}>
                        <SelectList
                            id='dateFilter'
                            value='all'
                            size='rg'
                            onChange={applyDateFilter}
                            dropDownList={logDates}
                            variant='roundedBorder'
                            disableUnderline={true}
                        />
                    </div>
                    <IconPrint className={css.printIcon} onClick={() => printCV()} />
                </div>
                <hr className={css.logHr}></hr>
                <span className={css.logHeader}>
                    <div className={css.logDescArea}>
                        <div id='logDescLit' className={css.logDescLit} onClick={moveFocusToDesc}>
                            {strings.EventDescription}
                        </div>
                        {descSortIcon}
                    </div>
                    <div className={css.logDateArea}>
                        <div id='logDateLit' className={css.logDateLit} onClick={moveFocusToDate}>
                            {strings.CVDate}
                        </div>
                        {dateSortIcon}
                    </div>
                    <div className={css.logTimeArea}>
                        <div id='logTimeLit' className={css.logTimeLit} onClick={moveFocusToTime}>
                            {strings.CVTime}
                        </div>
                        {timeSortIcon}
                    </div>
                    <div className={css.logUserArea}>
                        <div id='logUserLit' className={css.logUserLit} onClick={moveFocusToUser}>
                            {strings.ERAUser}
                        </div>
                        {userSortIcon}
                    </div>
                </span>
                <ScrollBar scrollVertical>
                    <div className={css.logs}>
                        {logs}
                    </div>
                </ScrollBar>
            </div>;
    }

    return (
        <div>
            <span className={css.header}>
                <div className={css.headerLit}>{strings.ViewConnectedVehicleLog}</div>
                <IconSeeMore className={css.icon} onClick={() => toggleLog()} />
            </span>
            <Dialog
                cssClasses={{
                    content: css.content,
                    paper: css.paper,
                    header: css.LightBoxHeader,
                }}
                isOpen={logOpen}
                anchorOrigin={{
                    vertical: 'top',
                }}
                header={strings.ConnectedVehicleLog}
                hideSave={true}
                hideCancel={true}
                disableUpperCase={true}
                onCancel={() => toggleLog()}
                onBackDropClick={() => toggleLog()}
                width={'full'}
            >
                <div>
                    {logArea}
                </div>
            </Dialog>
        </div>
    );
};

export default CVLog;